import { API_BASE, API } from "@/config/apiBase";
import React, { useState, useEffect } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, Mail, Lock, Eye, EyeOff, User, ArrowLeft, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import axios from "axios";


// Log API base for debugging

const RegisterPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Check for invite token on mount and pre-fill email if from invite
  useEffect(() => {
    const inviteId = searchParams.get('invite');
    if (inviteId) {
      console.log("[Register] Preserving family invite from URL:", inviteId);
      localStorage.setItem("pendingFamilyInvite", inviteId);
    }
    
    // Pre-fill email if provided in URL
    const inviteEmail = searchParams.get('email');
    if (inviteEmail) {
      setEmail(inviteEmail);
    }
  }, [searchParams]);

  const handleRegister = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error("Please accept the Terms of Service and Privacy Policy");
      return;
    }
    
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/auth/register`, { 
        name, 
        email, 
        password,
        accepted_terms: true,
        accepted_privacy: true
      });
      
      // Store token in localStorage
      if (response.data.token) {
        localStorage.setItem("token", response.data.token);
        console.log("[Auth] Token stored in localStorage");
      }
      
      // Check for pending family invite - redirect to settings if exists
      const pendingInvite = localStorage.getItem("pendingFamilyInvite");
      if (pendingInvite) {
        console.log("[Auth] Pending family invite found, redirecting to settings");
        toast.success("Welcome! Processing your family invite...");
        navigate("/settings?tab=family", { state: { user: response.data }, replace: true });
      } else {
        toast.success("Welcome to OuttaTouch!");
        navigate("/dashboard", { state: { user: response.data }, replace: true });
      }
    } catch (error) {
      const message = error.response?.data?.detail || "Registration failed";
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  // Direct Google OAuth - redirects to our backend which handles Google OAuth
  const handleGoogleSignup = () => {
    window.location.href = `${API_BASE}/api/auth/google/login`;
  };

  const benefits = [
    "Track all your important relationships",
    "Never forget to call someone again",
    "Build meaningful connection streaks",
    "Journal your conversations",
    "Free forever for personal use"
  ];

  return (
    <div className="min-h-screen bg-[#FDFBF7] flex">
      {/* Left side - Benefits */}
      <div className="hidden lg:flex flex-1 items-center justify-center bg-[#E07A5F] p-12">
        <motion.div 
          className="max-w-lg text-white"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          <h2 className="text-4xl font-bold mb-8" style={{ fontFamily: 'Fraunces, serif' }}>
            Start Staying in Touch Today
          </h2>
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <motion.li 
                key={index}
                className="flex items-center gap-3"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <CheckCircle className="w-6 h-6 flex-shrink-0" />
                <span className="text-lg">{benefit}</span>
              </motion.li>
            ))}
          </ul>
          <div className="mt-12">
            <img 
              src="https://images.pexels.com/photos/2072598/pexels-photo-2072598.jpeg" 
              alt="Connection"
              className="rounded-2xl shadow-xl opacity-90"
            />
          </div>
        </motion.div>
      </div>

      {/* Right side - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/" className="inline-flex items-center text-[#6D6F7C] hover:text-[#3D405B] mb-8 transition-colors" data-testid="back-link">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to home
          </Link>

          <div className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 bg-[#E07A5F] rounded-full flex items-center justify-center">
              <Phone className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
              Create Account
            </h1>
          </div>

          <form onSubmit={handleRegister} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-[#3D405B]">Your Name</Label>
              <div className="relative">
                <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-12 bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl h-14 text-[#3D405B]"
                  placeholder="John Doe"
                  required
                  data-testid="name-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-[#3D405B]">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-12 bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl h-14 text-[#3D405B]"
                  placeholder="your@email.com"
                  required
                  data-testid="email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-[#3D405B]">Password</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#9CA3AF]" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-12 pr-12 bg-white border-2 border-[#F4F1EA] focus:border-[#E07A5F] rounded-xl h-14 text-[#3D405B]"
                  placeholder="Min 6 characters"
                  required
                  minLength={6}
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#3D405B] transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Terms & Privacy Consent Checkbox */}
            <div className="flex items-start gap-3">
              <Checkbox
                id="acceptTerms"
                checked={acceptedTerms}
                onCheckedChange={(checked) => setAcceptedTerms(checked)}
                className="mt-1 border-[#E07A5F] data-[state=checked]:bg-[#E07A5F] data-[state=checked]:border-[#E07A5F]"
                data-testid="terms-checkbox"
              />
              <Label htmlFor="acceptTerms" className="text-sm text-[#6D6F7C] leading-relaxed cursor-pointer">
                By creating an account, you agree to the{" "}
                <Link to="/terms" className="text-[#E07A5F] hover:underline font-medium" target="_blank">
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link to="/privacy" className="text-[#E07A5F] hover:underline font-medium" target="_blank">
                  Privacy Policy
                </Link>
                .
              </Label>
            </div>

            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full h-14 text-lg font-semibold shadow-lg hover:shadow-xl transition-all"
              data-testid="register-button"
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                  Creating account...
                </span>
              ) : (
                "Create Account"
              )}
            </Button>
          </form>

          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-[#F4F1EA]"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-[#FDFBF7] px-4 text-[#9CA3AF]">or sign up with</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            onClick={handleGoogleSignup}
            className="w-full border-2 border-[#F4F1EA] hover:bg-[#F4F1EA] rounded-full h-14 text-[#3D405B] font-medium"
            data-testid="google-signup-button"
          >
            <svg className="w-5 h-5 mr-3" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </Button>

          <p className="text-center text-[#6D6F7C] mt-8">
            Already have an account?{" "}
            <Link to="/login" className="text-[#E07A5F] font-semibold hover:underline" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RegisterPage;
